import { useContext, useEffect, useState } from "react";
import { updateOnboardingStep } from "../dataProvider";
import { styled } from "@mui/material/styles";
import {
  TabbedForm,
  FormTab,
  Edit,
  TextInput,
  required,
  ImageField,
  ImageInput,
  useDataProvider,
  SelectArrayInput,
  FunctionField,
  BooleanInput,
  FormDataConsumer,
  SelectInput,
  useGetOne,
  Toolbar,
  SaveButton,
} from "react-admin";
import { OnboardingContext } from "../../contexts/OnboardingContext";
const PREFIX = "ProfileEdit";

const classes = {
  inlineBlock: `${PREFIX}-inlineBlock`,
};

const StyledEdit = styled(Edit)({
  [`& .${classes.inlineBlock}`]: {
    display: "inline-flex",
    marginRight: "1rem",
  },
});

function formatLogo(value) {
  if (!value) {
    return null;
  }

  if (typeof value === "string") {
    // Value is the url string from the backend, wrap it in an object so the form input can handle it
    return { src: value };
  }

  if (value.rawFile) {
    // New image is selected, create preview URL
    return {
      src: URL.createObjectURL(value.rawFile),
      title: value.rawFile.name,
    };
  }

  // Return as is if it's already formatted correctly
  return value;
}

function formatQR(value) {
  if (!value) return null;

  if (typeof value === "string") {
    return { src: value };
  }

  if (value.rawFile) {
    // New image is selected, create preview URL
    return {
      src: URL.createObjectURL(value.rawFile),
      title: value.rawFile.name,
    };
  }

  return value;
}

const ProfileEdit = ({ ...props }) => {
  const dataProvider = useDataProvider();
  const [services, setServices] = useState([]);

  useEffect(() => {
    dataProvider
      .getList("service")
      .then(({ data }) => {
        setServices(data);
      })
      .catch(() => {
        // Error fetching services - handle silently
      });
  }, [dataProvider]);

  const validateSelection = (value) => {
    if (value && value.length > 3) {
      return "Solo puedes seleccionar hasta 3 servicios";
    }
    return undefined;
  };
  const profile = JSON.parse(localStorage.getItem("profile") || "{}");
  const providerId = profile?.role === "admin-sucursal" ? profile?.employer?._id : profile?._id;
  const { data: provider } = useGetOne("providers", { id: providerId });
  // Handler para guardar y actualizar onboarding
  const { setOnboardingSteps, onboardingSteps, checkAndTriggerOnboarding } = useContext(OnboardingContext);
  const handleSave = async (values, redirect) => {
    try {
      // Primero guarda el perfil
      console.log("Valores: ", values);
      await dataProvider.update("profile", { id: values._id, data: values });
      // Luego actualiza el onboarding step 2
      // await updateOnboardingStep({ userId: values._id, step: 2 });
      await updateOnboardingStep(
        { userId: values._id, step: 2 },
        { setOnboardingSteps, checkAndTriggerOnboarding, onboardingSteps }
      );
    } catch (err) {
      if (err?.status && err.status >= 400) {
        // Error actualizando el perfil o el onboarding - handle silently
      }
    }
    // Continúa con el guardado normal
    if (props.onSave) {
      props.onSave(values, redirect);
    }
  };

  return (
    <StyledEdit
      {...props}
      id="my-profile"
      resource="profile"
      basePath="/my-profile"
      redirect={false}
      title="Mi Perfil"
      data-cy="profile-card"
    >
      <TabbedForm
        onSubmit={handleSave}
        toolbar={
          <Toolbar>
            <SaveButton data-cy="profile-save" />
          </Toolbar>
        }
      >
        <FormTab label="Datos Generales">
          <ImageInput
            data-tour="logo-empresa"
            // validate={[required()]}
            format={formatLogo}
            source="picture"
            label="Logo de Empresa"
            accept=".jpeg,.jpg,.png,.jfif"
            placeholder={<p>Arrastra tu imagen o haz clic aquí.</p>}
            sx={{
              width: 300,
              border: "2px dashed #ccc",
              borderRadius: 2,
              backgroundColor: "#fafafa",
              color: "#666",
              p: 1,
              marginBottom: 2,
              textAlign: "center",
              transition: "border-color 0.3s, background-color 0.3s",
              "&:hover": {
                borderColor: "#3f51b5",
                backgroundColor: "#f5f5f5",
              },
              "& .RaImageInput-dropZone": {
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 140,
                fontSize: "1rem",
              },
            }}
          >
            <ImageField source="src" title="title" />
          </ImageInput>
          {provider?.whatsappLinked && (
            <>
              <div data-tour="habilitar-qr">
                <BooleanInput
                  label="Habilitar envío de QR para pagos"
                  source="paymentQR.isActive"
                  defaultValue={false}
                />
              </div>
              <FormDataConsumer>
                {({ formData, ...rest }) =>
                  formData.paymentQR.isActive && (
                    <>
                      <ImageInput
                        data-tour="qr-pagos"
                        format={formatQR}
                        source="paymentQR.image"
                        label="QR pagos"
                        accept=".jpeg,.jpg,.png,.jfif"
                        placeholder="Arrastra tu imagen o haz clic aquí."
                        sx={{
                          height: 250,
                          width: 300,
                          border: "2px dashed #ccc",
                          borderRadius: 2,
                          backgroundColor: "#fafafa",
                          color: "#666",
                          p: 1,
                          mb: 3,
                          textAlign: "center",
                          transition: "border-color 0.3s, background-color 0.3s",
                          "&:hover": {
                            borderColor: "#3f51b5",
                            backgroundColor: "#f5f5f5",
                          },
                          "& .RaImageInput-dropZone": {
                            cursor: "pointer",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 140,
                            fontSize: "1rem",
                          },
                        }}
                        {...rest}
                      >
                        <ImageField source="src" title="QR" />
                      </ImageInput>

                      <div data-tour="mensaje-qr-confirmado" style={{ paddingTop: "16px" }}>
                        <TextInput
                          source="paymentQR.message"
                          defaultValue={"QR PAGO - Reserva Confirmada"}
                          minRows={1}
                          multiline
                          label="Mensaje para reserva confirmada"
                          sx={{ width: 300 }}
                        />
                      </div>

                      <div data-tour="mensaje-qr-finalizado" style={{ paddingTop: "16px" }}>
                        <TextInput
                          source="paymentQR.messageFinished"
                          defaultValue={"QR PAGO - Servicio Finalizado"}
                          minRows={1}
                          multiline
                          label="Mensaje para servicio finalizado"
                          sx={{ width: 300 }}
                        />
                      </div>

                      <div style={{ paddingTop: "16px" }}>
                        <BooleanInput
                          label="Enviar imagen QR con mensaje de confirmado"
                          source="paymentQR.sendImageConfirmed"
                          defaultValue={true}
                        />
                      </div>

                      <div style={{ paddingTop: "8px" }}>
                        <BooleanInput
                          label="Enviar imagen QR con mensaje de finalizado"
                          source="paymentQR.sendImageFinished"
                          defaultValue={true}
                        />
                      </div>
                    </>
                  )
                }
              </FormDataConsumer>
            </>
          )}
          <div data-tour="datos-generales" style={{ display: "flex", flexDirection: "column" }}>
            <TextInput
              data-tour="nombre-empresa"
              source="first_name"
              validate={required()}
              label="Nombre de Empresa"
              data-cy="profile-name"
            />
            <TextInput
              data-tour="razon-social"
              source="business_name"
              validate={required()}
              label="Razón Social"
              data-cy="profile-business"
            />
            <TextInput
              data-tour="telefono"
              source="phone"
              validate={required()}
              label="Teléfono"
              data-cy="profile-phone"
            />
            <SelectInput
              source="intervalMinutes"
              label="Intervalo de Reservas (minutos)"
              defaultValue={30}
              choices={[
                { id: 15, name: "15 minutos" },
                { id: 30, name: "30 minutos" },
                { id: 60, name: "60 minutos" },
              ]}
              validate={required()}
            />
            <TextInput source="CI_NIT" validate={required()} label="NIT" data-cy="profile-nit" />
            <SelectArrayInput
              source="recommendedServices"
              choices={services}
              optionValue="_id"
              optionText="name"
              style={{ width: 200 }}
              validate={[validateSelection]}
              label="Servicios Recomendados"
            />
            <div data-tour="sitio-web">
              <FunctionField
                label="Web URL"
                textAlign="center"
                render={(user) => process.env.REACT_APP_WEB_CLIENT_URL + "/" + user.slugUrl}
              />
            </div>
          </div>
        </FormTab>
      </TabbedForm>
    </StyledEdit>
  );
};

export default ProfileEdit;
